/**
 * Copyright (c) 2026 Zoldier & LCN Softwares All rights reserved.
 *
 * This software is the confidential and proprietary information of Zoldier & LCN Software.
 * You shall not disclose such confidential information and shall use it only
 * in accordance with the terms of the license agreement you entered into with
 * Zoldier & LCN Software.
 */

/**
 * Landing Page Component
 *
 * Public-facing marketing page for ZIA Platforms. Dark-first design with
 * geometric futuristic aesthetics, inline SVG illustrations, scroll-reveal
 * animations, and full i18n support (EN/ES).
 *
 * All admin CTAs are plain `<a>` tags that perform a hard navigation to the
 * admin application URL (`VITE_ADMIN_URL + '/login'`). This module contains
 * no React Router, no Redux, no API calls, and no authentication logic — it
 * is a static marketing page.
 *
 * @module modules/landing/components/landing-page/LandingPage
 * @since 1.0.1
 * @version 1.0.0
 * @author Eng. Letzer Cartagena Negrón
 */

import {
  useState,
  useEffect,
  useRef,
  useCallback,
  type JSX,
  type ReactElement,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocale } from '../../../../hooks/useLocale';
import './landing-page.css';

/**
 * Resolved admin login URL used by every "Sign In" / CTA redirect on the page.
 *
 * Temporarily hardcoded to `/` so that clicking any admin CTA simply reloads
 * the landing page. The real target (`https://admin.ziaplatforms.com/login`)
 * will be wired up once the admin subdomain is deployed; at that point this
 * constant should be rebuilt from `VITE_ADMIN_URL` as documented in `.env.example`.
 *
 * @since 1.0.1
 * @version 1.0.1
 * @author Eng. Letzer Cartagena Negrón
 */
const ADMIN_LOGIN_URL = '/';

/**
 * Hero Section SVG Illustration
 *
 * Renders an abstract neural-network grid with concentric hexagonal rings,
 * radial connector lines, scattered nodes, and floating particles. Used as
 * the decorative background for the landing page hero section.
 *
 * @returns An inline SVG illustration element.
 * @since 1.0.1
 * @version 1.0.0
 * @author Eng. Letzer Cartagena Negrón
 */
function HeroSvg(): JSX.Element {
  return (
    <svg className="landing-hero-svg" viewBox="0 0 900 400" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <defs>
        <linearGradient id="hero-grad" x1="0" y1="0" x2="900" y2="400">
          <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.4" />
          <stop offset="50%" stopColor="#06b6d4" stopOpacity="0.2" />
          <stop offset="100%" stopColor="#8b5cf6" stopOpacity="0.3" />
        </linearGradient>
      </defs>
      <polygon points="450,60 600,130 600,270 450,340 300,270 300,130" stroke="url(#hero-grad)" strokeWidth="0.5" fill="none" opacity="0.6" />
      <polygon points="450,100 570,155 570,245 450,300 330,245 330,155" stroke="url(#hero-grad)" strokeWidth="0.4" fill="none" opacity="0.4" />
      <polygon points="450,140 540,180 540,220 450,260 360,220 360,180" stroke="url(#hero-grad)" strokeWidth="0.3" fill="none" opacity="0.3" />
      <line x1="450" y1="60" x2="450" y2="20" stroke="#3b82f6" strokeWidth="0.3" opacity="0.3" />
      <line x1="600" y1="130" x2="680" y2="90" stroke="#06b6d4" strokeWidth="0.3" opacity="0.25" />
      <line x1="600" y1="270" x2="680" y2="310" stroke="#06b6d4" strokeWidth="0.3" opacity="0.25" />
      <line x1="300" y1="130" x2="220" y2="90" stroke="#8b5cf6" strokeWidth="0.3" opacity="0.25" />
      <line x1="300" y1="270" x2="220" y2="310" stroke="#8b5cf6" strokeWidth="0.3" opacity="0.25" />
      <line x1="450" y1="340" x2="450" y2="380" stroke="#3b82f6" strokeWidth="0.3" opacity="0.3" />
      <circle cx="450" cy="200" r="3" fill="#3b82f6" opacity="0.6" />
      <circle cx="450" cy="60" r="2" fill="#3b82f6" opacity="0.4" />
      <circle cx="600" cy="130" r="2" fill="#06b6d4" opacity="0.4" />
      <circle cx="600" cy="270" r="2" fill="#06b6d4" opacity="0.4" />
      <circle cx="300" cy="130" r="2" fill="#8b5cf6" opacity="0.4" />
      <circle cx="300" cy="270" r="2" fill="#8b5cf6" opacity="0.4" />
      <circle cx="450" cy="340" r="2" fill="#3b82f6" opacity="0.4" />
      <circle cx="150" cy="100" r="1" fill="#3b82f6" opacity="0.2" />
      <circle cx="750" cy="320" r="1" fill="#06b6d4" opacity="0.2" />
      <circle cx="100" cy="300" r="1.5" fill="#8b5cf6" opacity="0.15" />
      <circle cx="800" cy="80" r="1.5" fill="#3b82f6" opacity="0.15" />
      <circle cx="200" cy="200" r="0.8" fill="#06b6d4" opacity="0.2" />
      <circle cx="700" cy="200" r="0.8" fill="#8b5cf6" opacity="0.2" />
      <path d="M 350 80 A 150 150 0 0 1 550 80" stroke="#3b82f6" strokeWidth="0.3" fill="none" opacity="0.2" />
      <path d="M 350 320 A 150 150 0 0 0 550 320" stroke="#06b6d4" strokeWidth="0.3" fill="none" opacity="0.2" />
    </svg>
  );
}

/**
 * Product Icon SVG — Embedded
 *
 * Renders a 32x32 icon representing the ZIA Embedded product using a
 * code-brackets visual metaphor with a slash divider.
 *
 * @returns An inline SVG icon element.
 * @since 1.0.1
 * @version 1.0.0
 * @author Eng. Letzer Cartagena Negrón
 */
function ProductEmbeddedSvg(): JSX.Element {
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <rect x="4" y="6" width="24" height="20" rx="2" stroke="currentColor" strokeWidth="1.5" />
      <path d="M12 14L9 16L12 18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M20 14L23 16L20 18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <line x1="17" y1="12" x2="15" y2="20" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

/**
 * Product Icon SVG — Web
 *
 * Renders a 32x32 icon representing the ZIA Web product using a globe
 * with latitude and longitude lines.
 *
 * @returns An inline SVG icon element.
 * @since 1.0.1
 * @version 1.0.0
 * @author Eng. Letzer Cartagena Negrón
 */
function ProductWebSvg(): JSX.Element {
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <circle cx="16" cy="16" r="11" stroke="currentColor" strokeWidth="1.5" />
      <ellipse cx="16" cy="16" rx="5" ry="11" stroke="currentColor" strokeWidth="1" opacity="0.6" />
      <line x1="5" y1="16" x2="27" y2="16" stroke="currentColor" strokeWidth="1" opacity="0.6" />
      <line x1="7" y1="10" x2="25" y2="10" stroke="currentColor" strokeWidth="0.75" opacity="0.4" />
      <line x1="7" y1="22" x2="25" y2="22" stroke="currentColor" strokeWidth="0.75" opacity="0.4" />
    </svg>
  );
}

/**
 * Product Icon SVG — Admin
 *
 * Renders a 32x32 icon representing the ZIA Admin product using a
 * dashboard layout with panels and traffic-light dots.
 *
 * @returns An inline SVG icon element.
 * @since 1.0.1
 * @version 1.0.0
 * @author Eng. Letzer Cartagena Negrón
 */
function ProductAdminSvg(): JSX.Element {
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <rect x="4" y="4" width="24" height="24" rx="2" stroke="currentColor" strokeWidth="1.5" />
      <line x1="4" y1="10" x2="28" y2="10" stroke="currentColor" strokeWidth="1" opacity="0.6" />
      <rect x="7" y="13" width="8" height="5" rx="1" stroke="currentColor" strokeWidth="1" opacity="0.7" />
      <rect x="7" y="21" width="8" height="4" rx="1" stroke="currentColor" strokeWidth="1" opacity="0.5" />
      <line x1="19" y1="14" x2="25" y2="14" stroke="currentColor" strokeWidth="1" opacity="0.5" />
      <line x1="19" y1="17" x2="23" y2="17" stroke="currentColor" strokeWidth="1" opacity="0.4" />
      <line x1="19" y1="22" x2="25" y2="22" stroke="currentColor" strokeWidth="1" opacity="0.5" />
      <circle cx="6" cy="7" r="1" fill="currentColor" opacity="0.4" />
      <circle cx="9" cy="7" r="1" fill="currentColor" opacity="0.4" />
    </svg>
  );
}

/**
 * Product Icon SVG — LBrain
 *
 * Renders a 32x32 icon representing the ZIA LBrain product using a
 * neural-topology cluster of connected nodes with ambient sparkle particles.
 *
 * @returns An inline SVG icon element.
 * @since 1.0.1
 * @version 1.0.0
 * @author Eng. Letzer Cartagena Negrón
 */
function ProductLbrainSvg(): JSX.Element {
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <circle cx="16" cy="10" r="3" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="9" cy="18" r="2.5" stroke="currentColor" strokeWidth="1.2" />
      <circle cx="23" cy="18" r="2.5" stroke="currentColor" strokeWidth="1.2" />
      <circle cx="16" cy="25" r="2" stroke="currentColor" strokeWidth="1" />
      <line x1="14" y1="12.5" x2="10.5" y2="16" stroke="currentColor" strokeWidth="1" opacity="0.6" />
      <line x1="18" y1="12.5" x2="21.5" y2="16" stroke="currentColor" strokeWidth="1" opacity="0.6" />
      <line x1="11" y1="19.5" x2="14.5" y2="23.5" stroke="currentColor" strokeWidth="0.8" opacity="0.5" />
      <line x1="21" y1="19.5" x2="17.5" y2="23.5" stroke="currentColor" strokeWidth="0.8" opacity="0.5" />
      <circle cx="6" cy="8" r="0.8" fill="currentColor" opacity="0.3" />
      <circle cx="26" cy="8" r="0.8" fill="currentColor" opacity="0.3" />
      <circle cx="4" cy="14" r="0.5" fill="currentColor" opacity="0.2" />
      <circle cx="28" cy="14" r="0.5" fill="currentColor" opacity="0.2" />
    </svg>
  );
}

/**
 * Product Icon SVG — MCP
 *
 * Renders a 32x32 icon representing the ZIA MCP product using a
 * hub-and-spoke topology with four satellite nodes and directional hints.
 *
 * @returns An inline SVG icon element.
 * @since 1.0.1
 * @version 1.0.0
 * @author Eng. Letzer Cartagena Negrón
 */
function ProductMcpSvg(): JSX.Element {
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <circle cx="16" cy="16" r="4" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="6" cy="8" r="2.5" stroke="currentColor" strokeWidth="1" opacity="0.7" />
      <circle cx="26" cy="8" r="2.5" stroke="currentColor" strokeWidth="1" opacity="0.7" />
      <circle cx="6" cy="24" r="2.5" stroke="currentColor" strokeWidth="1" opacity="0.7" />
      <circle cx="26" cy="24" r="2.5" stroke="currentColor" strokeWidth="1" opacity="0.7" />
      <line x1="13" y1="13" x2="8" y2="9.5" stroke="currentColor" strokeWidth="1" opacity="0.5" />
      <line x1="19" y1="13" x2="24" y2="9.5" stroke="currentColor" strokeWidth="1" opacity="0.5" />
      <line x1="13" y1="19" x2="8" y2="22.5" stroke="currentColor" strokeWidth="1" opacity="0.5" />
      <line x1="19" y1="19" x2="24" y2="22.5" stroke="currentColor" strokeWidth="1" opacity="0.5" />
      <circle cx="10" cy="11" r="0.7" fill="currentColor" opacity="0.4" />
      <circle cx="22" cy="11" r="0.7" fill="currentColor" opacity="0.4" />
      <circle cx="10" cy="21" r="0.7" fill="currentColor" opacity="0.4" />
      <circle cx="22" cy="21" r="0.7" fill="currentColor" opacity="0.4" />
    </svg>
  );
}

/**
 * Product Icon SVG — Distribution
 *
 * Renders a 32x32 icon representing the ZIA Distribution product using a
 * package box with a downward arrow indicating delivery or download.
 *
 * @returns An inline SVG icon element.
 * @since 1.0.1
 * @version 1.0.0
 * @author Eng. Letzer Cartagena Negrón
 */
function ProductDistributionSvg(): JSX.Element {
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <rect x="6" y="10" width="20" height="16" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
      <polyline points="6,16 16,12 26,16" stroke="currentColor" strokeWidth="1" opacity="0.6" />
      <line x1="16" y1="6" x2="16" y2="12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <polyline points="13,9 16,12 19,9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <line x1="16" y1="16" x2="16" y2="26" stroke="currentColor" strokeWidth="0.8" opacity="0.4" strokeDasharray="2 2" />
    </svg>
  );
}

/**
 * Product Icon SVG — RAG
 *
 * Renders a 32x32 icon representing the ZIA RAG product using a stacked
 * document visual with a magnifier glass overlay to convey retrieval.
 *
 * @returns An inline SVG icon element.
 * @since 1.0.1
 * @version 1.0.0
 * @author Eng. Letzer Cartagena Negrón
 */
function ProductRagSvg(): JSX.Element {
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <rect x="8" y="4" width="16" height="20" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
      <rect x="6" y="6" width="16" height="20" rx="1.5" stroke="currentColor" strokeWidth="1" opacity="0.3" />
      <line x1="12" y1="10" x2="20" y2="10" stroke="currentColor" strokeWidth="1" opacity="0.5" />
      <line x1="12" y1="13" x2="20" y2="13" stroke="currentColor" strokeWidth="1" opacity="0.4" />
      <line x1="12" y1="16" x2="17" y2="16" stroke="currentColor" strokeWidth="1" opacity="0.3" />
      <circle cx="22" cy="22" r="4.5" stroke="currentColor" strokeWidth="1.5" />
      <line x1="25.2" y1="25.2" x2="28" y2="28" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="13" cy="20" r="0.8" fill="currentColor" opacity="0.4" />
      <circle cx="16" cy="20" r="0.8" fill="currentColor" opacity="0.4" />
      <circle cx="19" cy="20" r="0.8" fill="currentColor" opacity="0.4" />
    </svg>
  );
}

/**
 * Product Icon SVG — Skills
 *
 * Renders a 32x32 icon representing the ZIA Skills product using a
 * four-quadrant grid with puzzle tabs and a guard shield detail.
 *
 * @returns An inline SVG icon element.
 * @since 1.0.1
 * @version 1.0.0
 * @author Eng. Letzer Cartagena Negrón
 */
function ProductSkillsSvg(): JSX.Element {
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <rect x="5" y="5" width="22" height="22" rx="2" stroke="currentColor" strokeWidth="1.5" />
      <line x1="5" y1="16" x2="27" y2="16" stroke="currentColor" strokeWidth="0.8" opacity="0.4" />
      <line x1="16" y1="5" x2="16" y2="27" stroke="currentColor" strokeWidth="0.8" opacity="0.4" />
      <path d="M16 12.5C16 11 17.5 10 19 10C20.5 10 22 11 22 12.5" stroke="currentColor" strokeWidth="1" opacity="0.6" fill="none" />
      <path d="M10 16C11 16 12.5 17.5 12.5 19C12.5 20.5 11 22 10 22" stroke="currentColor" strokeWidth="1" opacity="0.6" fill="none" />
      <path d="M21 19L23.5 20V22.5C23.5 24 22.5 25 21 25.5C19.5 25 18.5 24 18.5 22.5V20L21 19Z" stroke="currentColor" strokeWidth="0.9" fill="none" opacity="0.7" />
      <circle cx="10" cy="10" r="2" stroke="currentColor" strokeWidth="1" opacity="0.5" />
      <circle cx="10" cy="10" r="0.8" fill="currentColor" opacity="0.5" />
    </svg>
  );
}

/**
 * Product Icon SVG — Tuning
 *
 * Renders a 32x32 icon representing the ZIA Tuning product using three
 * vertical slider tracks with knobs at staggered positions and a
 * waveform hint at the top.
 *
 * @returns An inline SVG icon element.
 * @since 1.0.1
 * @version 1.0.0
 * @author Eng. Letzer Cartagena Negrón
 */
function ProductTuningSvg(): JSX.Element {
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <line x1="8" y1="8" x2="8" y2="26" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <line x1="16" y1="6" x2="16" y2="26" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <line x1="24" y1="8" x2="24" y2="26" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <rect x="5.5" y="12" width="5" height="3" rx="1" fill="currentColor" opacity="0.6" />
      <rect x="13.5" y="18" width="5" height="3" rx="1" fill="currentColor" opacity="0.6" />
      <rect x="21.5" y="14" width="5" height="3" rx="1" fill="currentColor" opacity="0.6" />
      <path d="M6 5C8 3 10 5 12 4C14 3 16 5 18 4C20 3 22 5 24 4C26 3 27 5 28 4" stroke="currentColor" strokeWidth="0.8" opacity="0.3" strokeLinecap="round" />
    </svg>
  );
}

/**
 * Differentiator SVG — Privacy
 *
 * Renders a decorative 200x80 SVG for the Privacy differentiator card,
 * depicting a shield-and-lock motif with bounded data points.
 *
 * @returns An inline decorative SVG element.
 * @since 1.0.1
 * @version 1.0.0
 * @author Eng. Letzer Cartagena Negrón
 */
function DiffPrivacySvg(): JSX.Element {
  return (
    <svg className="landing-diff-svg" viewBox="0 0 200 80" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M100 10 L130 22 L130 48 Q130 65 100 75 Q70 65 70 48 L70 22 Z" stroke="#3b82f6" strokeWidth="0.8" fill="none" opacity="0.4" />
      <rect x="92" y="34" width="16" height="12" rx="2" stroke="#3b82f6" strokeWidth="0.8" fill="none" opacity="0.6" />
      <circle cx="100" cy="32" r="5" stroke="#3b82f6" strokeWidth="0.7" fill="none" opacity="0.5" />
      <circle cx="90" cy="45" r="1" fill="#3b82f6" opacity="0.3" />
      <circle cx="110" cy="42" r="1" fill="#3b82f6" opacity="0.3" />
      <circle cx="100" cy="55" r="1" fill="#3b82f6" opacity="0.3" />
      <line x1="40" y1="10" x2="40" y2="75" stroke="#3b82f6" strokeWidth="0.4" strokeDasharray="3 4" opacity="0.2" />
      <line x1="160" y1="10" x2="160" y2="75" stroke="#3b82f6" strokeWidth="0.4" strokeDasharray="3 4" opacity="0.2" />
    </svg>
  );
}

/**
 * Differentiator SVG — Configure
 *
 * Renders a decorative 200x80 SVG for the Configure differentiator card,
 * depicting a settings panel with three slider tracks and knobs.
 *
 * @returns An inline decorative SVG element.
 * @since 1.0.1
 * @version 1.0.0
 * @author Eng. Letzer Cartagena Negrón
 */
function DiffConfigureSvg(): JSX.Element {
  return (
    <svg className="landing-diff-svg" viewBox="0 0 200 80" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <rect x="50" y="15" width="100" height="50" rx="3" stroke="#10b981" strokeWidth="0.6" fill="none" opacity="0.3" />
      <line x1="65" y1="30" x2="135" y2="30" stroke="#10b981" strokeWidth="0.8" opacity="0.3" />
      <circle cx="110" cy="30" r="3.5" fill="#10b981" opacity="0.5" />
      <line x1="65" y1="42" x2="135" y2="42" stroke="#10b981" strokeWidth="0.8" opacity="0.3" />
      <circle cx="85" cy="42" r="3.5" fill="#10b981" opacity="0.5" />
      <line x1="65" y1="54" x2="135" y2="54" stroke="#10b981" strokeWidth="0.8" opacity="0.3" />
      <circle cx="120" cy="54" r="3.5" fill="#10b981" opacity="0.5" />
    </svg>
  );
}

/**
 * Differentiator SVG — Bidirectional
 *
 * Renders a decorative 200x80 SVG for the Bidirectional differentiator card,
 * depicting two entity boxes connected by opposing arrows and signal pulses.
 *
 * @returns An inline decorative SVG element.
 * @since 1.0.1
 * @version 1.0.0
 * @author Eng. Letzer Cartagena Negrón
 */
function DiffBidirectionalSvg(): JSX.Element {
  return (
    <svg className="landing-diff-svg" viewBox="0 0 200 80" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <rect x="30" y="25" width="40" height="30" rx="3" stroke="#06b6d4" strokeWidth="0.8" fill="none" opacity="0.4" />
      <rect x="130" y="25" width="40" height="30" rx="3" stroke="#06b6d4" strokeWidth="0.8" fill="none" opacity="0.4" />
      <line x1="75" y1="35" x2="125" y2="35" stroke="#06b6d4" strokeWidth="0.8" opacity="0.5" />
      <polyline points="120,32 125,35 120,38" stroke="#06b6d4" strokeWidth="0.8" fill="none" opacity="0.5" />
      <line x1="125" y1="45" x2="75" y2="45" stroke="#06b6d4" strokeWidth="0.8" opacity="0.5" />
      <polyline points="80,42 75,45 80,48" stroke="#06b6d4" strokeWidth="0.8" fill="none" opacity="0.5" />
      <circle cx="95" cy="35" r="1.5" fill="#06b6d4" opacity="0.4" />
      <circle cx="105" cy="45" r="1.5" fill="#06b6d4" opacity="0.4" />
    </svg>
  );
}

/**
 * Differentiator SVG — Memory
 *
 * Renders a decorative 200x80 SVG for the Memory differentiator card,
 * depicting stacked elliptical layers growing upward with rising particles
 * to represent dream memory consolidation.
 *
 * @returns An inline decorative SVG element.
 * @since 1.0.1
 * @version 1.0.0
 * @author Eng. Letzer Cartagena Negrón
 */
function DiffMemorySvg(): JSX.Element {
  return (
    <svg className="landing-diff-svg" viewBox="0 0 200 80" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <ellipse cx="100" cy="60" rx="45" ry="10" stroke="#8b5cf6" strokeWidth="0.6" fill="none" opacity="0.3" />
      <ellipse cx="100" cy="48" rx="40" ry="9" stroke="#8b5cf6" strokeWidth="0.6" fill="none" opacity="0.4" />
      <ellipse cx="100" cy="37" rx="35" ry="8" stroke="#8b5cf6" strokeWidth="0.7" fill="none" opacity="0.5" />
      <ellipse cx="100" cy="27" rx="28" ry="7" stroke="#8b5cf6" strokeWidth="0.7" fill="none" opacity="0.6" />
      <ellipse cx="100" cy="18" rx="20" ry="6" stroke="#8b5cf6" strokeWidth="0.8" fill="none" opacity="0.7" />
      <circle cx="90" cy="12" r="1" fill="#8b5cf6" opacity="0.4" />
      <circle cx="100" cy="8" r="0.8" fill="#8b5cf6" opacity="0.3" />
      <circle cx="108" cy="10" r="1.2" fill="#8b5cf6" opacity="0.35" />
    </svg>
  );
}

// /**
//  * Pricing Feature Check Icon
//  *
//  * Renders a small 14x14 checkmark SVG used in pricing feature lists
//  * to indicate an included feature.
//  *
//  * @returns An inline SVG checkmark icon element.
//  * @since 1.0.1
//  * @version 1.0.0
//  * @author Eng. Letzer Cartagena Negrón
//  */
// function CheckIcon(): JSX.Element {
//   return (
//     <svg className="landing-pricing-check" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
//       <path d="M3 7L6 10L11 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
//     </svg>
//   );
// }

/**
 * Call-to-Action Section Background SVG
 *
 * Renders a decorative 600x120 SVG placed absolutely at the bottom of the
 * CTA banner section. Uses converging perspective lines and a focal circle
 * to create a subtle depth illusion at very low opacity.
 *
 * @returns An inline decorative SVG element.
 * @since 1.0.1
 * @version 1.0.0
 * @author Eng. Letzer Cartagena Negrón
 */
function CtaSvg(): JSX.Element {
  return (
    <svg className="landing-svg-illustration landing-cta-svg" viewBox="0 0 600 120" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <line x1="0" y1="120" x2="300" y2="30" stroke="#3b82f6" strokeWidth="0.5" />
      <line x1="600" y1="120" x2="300" y2="30" stroke="#06b6d4" strokeWidth="0.5" />
      <line x1="100" y1="120" x2="300" y2="50" stroke="#3b82f6" strokeWidth="0.3" />
      <line x1="500" y1="120" x2="300" y2="50" stroke="#06b6d4" strokeWidth="0.3" />
      <circle cx="300" cy="30" r="4" fill="#3b82f6" opacity="0.3" />
    </svg>
  );
}

/**
 * Scroll Reveal Hook
 *
 * Returns a ref that, when attached to a container element, observes all
 * direct children that carry the `landing-reveal` class. When an observed
 * element enters the viewport it receives the `landing-reveal--visible`
 * modifier class which triggers the CSS entrance animation.
 *
 * The IntersectionObserver is disconnected on cleanup to prevent memory leaks.
 *
 * @returns A ref to attach to the scroll container element.
 * @since 1.0.1
 * @version 1.0.0
 * @author Eng. Letzer Cartagena Negrón
 */
function useScrollReveal(): React.RefObject<HTMLElement | null> {
  const ref = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('landing-reveal--visible');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' },
    );

    const children = el.querySelectorAll('.landing-reveal');
    children.forEach((child) => observer.observe(child));
    observer.observe(el);

    return () => observer.disconnect();
  }, []);

  return ref;
}

/**
 * Supported product metadata keys rendered in the products grid.
 *
 * Each key corresponds to a translation path under `products.{key}` and a
 * CSS modifier under `.landing-product-icon--{key}`.
 *
 * @since 1.0.1
 * @version 1.0.0
 * @author Eng. Letzer Cartagena Negrón
 */
type ProductKey =
  | 'embedded'
  | 'web'
  | 'admin'
  | 'lbrain'
  | 'mcp'
  | 'distribution'
  | 'rag'
  | 'skills'
  | 'tuning';

/**
 * Static descriptor of a single product rendered in the products grid.
 *
 * @since 1.0.1
 * @version 1.0.0
 * @author Eng. Letzer Cartagena Negrón
 */
interface ProductDescriptor {
  readonly key: ProductKey;
  readonly Icon: () => JSX.Element;
  readonly colorClass: ProductKey;
}

/**
 * Supported differentiator keys rendered in the differentiators grid.
 *
 * Each key corresponds to a translation path under `differentiators.{key}`
 * and CSS modifiers under `.landing-diff-card--{key}` / `.landing-diff-icon--{key}`.
 *
 * @since 1.0.1
 * @version 1.0.0
 * @author Eng. Letzer Cartagena Negrón
 */
type DifferentiatorKey = 'privacy' | 'configure' | 'bidirectional' | 'memory';

/**
 * Static descriptor of a single differentiator card.
 *
 * @since 1.0.1
 * @version 1.0.0
 * @author Eng. Letzer Cartagena Negrón
 */
interface DifferentiatorDescriptor {
  readonly key: DifferentiatorKey;
  readonly Svg: () => JSX.Element;
  readonly colorClass: DifferentiatorKey;
}

// /**
//  * Supported pricing tier keys.
//  *
//  * @since 1.0.1
//  * @version 1.0.0
//  * @author Eng. Letzer Cartagena Negrón
//  */
// type PricingTierKey = 'free' | 'starter' | 'pro' | 'enterprise';

// /**
//  * Static descriptor of a single pricing tier card.
//  *
//  * @since 1.0.1
//  * @version 1.0.0
//  * @author Eng. Letzer Cartagena Negrón
//  */
// interface PricingTierDescriptor {
//   readonly key: PricingTierKey;
//   readonly recommended: boolean;
// }

/**
 * Footer column key identifying which set of link labels to render.
 *
 * @since 1.0.1
 * @version 1.0.0
 * @author Eng. Letzer Cartagena Negrón
 */
type FooterColumnKey = 'product' | 'company' | 'resources' | 'legal';

const FOOTER_COLUMN_KEYS: readonly FooterColumnKey[] = [
  'product',
  'company',
  'resources',
  'legal',
] as const;

const FOOTER_COL_LINKS: Record<FooterColumnKey, readonly string[]> = {
  product: ['embedded', 'web', 'admin', 'lbrain', 'mcp', 'distribution', 'rag', 'skills', 'tuning'],
  company: ['about', 'careers', 'blog', 'contact'],
  resources: ['docs', 'api', 'status', 'changelog'],
  legal: ['privacy', 'terms', 'security', 'gdpr'],
};

const TRUSTED_LOGOS: readonly string[] = [
  'SIMED',
  // 'VALTEC',
  // 'ORINOX',
  // 'SOLUMED',
  // 'QUANTIS',
  // 'AETHON',
];

const PRODUCTS: readonly ProductDescriptor[] = [
  { key: 'embedded', Icon: ProductEmbeddedSvg, colorClass: 'embedded' },
  { key: 'web', Icon: ProductWebSvg, colorClass: 'web' },
  { key: 'admin', Icon: ProductAdminSvg, colorClass: 'admin' },
  { key: 'lbrain', Icon: ProductLbrainSvg, colorClass: 'lbrain' },
  { key: 'mcp', Icon: ProductMcpSvg, colorClass: 'mcp' },
  { key: 'distribution', Icon: ProductDistributionSvg, colorClass: 'distribution' },
  { key: 'rag', Icon: ProductRagSvg, colorClass: 'rag' },
  { key: 'skills', Icon: ProductSkillsSvg, colorClass: 'skills' },
  { key: 'tuning', Icon: ProductTuningSvg, colorClass: 'tuning' },
];

const DIFFERENTIATORS: readonly DifferentiatorDescriptor[] = [
  { key: 'privacy', Svg: DiffPrivacySvg, colorClass: 'privacy' },
  { key: 'configure', Svg: DiffConfigureSvg, colorClass: 'configure' },
  { key: 'bidirectional', Svg: DiffBidirectionalSvg, colorClass: 'bidirectional' },
  { key: 'memory', Svg: DiffMemorySvg, colorClass: 'memory' },
];

// const PRICING_TIERS: readonly PricingTierDescriptor[] = [
//   { key: 'free', recommended: false },
//   { key: 'starter', recommended: false },
//   { key: 'pro', recommended: true },
//   { key: 'enterprise', recommended: false },
// ];

/**
 * LandingPage
 *
 * Top-level component for the public marketing landing page. Wires up the
 * navbar, hero, products grid, differentiators, pricing, CTA banner, and
 * footer. Manages the mobile menu open/closed state and smooth scroll-to
 * section navigation. Reads all copy from the `landing` i18next namespace.
 *
 * @returns The complete landing page React element tree.
 * @since 1.0.1
 * @version 1.0.0
 * @author Eng. Letzer Cartagena Negrón
 */
export function LandingPage(): ReactElement {
  const { t } = useTranslation('landing');
  const { toggleLocale } = useLocale();
  const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);

  const heroRef = useScrollReveal();
  const productsRef = useScrollReveal();
  const diffRef = useScrollReveal();
  // const pricingRef = useScrollReveal();
  const ctaRef = useScrollReveal();

  const scrollTo = useCallback((id: string): void => {
    setMobileMenuOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  return (
    <div className="landing">
      <div className="landing-ambient" aria-hidden="true">
        <div className="landing-ambient-orb-1" />
        <div className="landing-ambient-orb-2" />
        <div className="landing-ambient-orb-3" />
      </div>
      <div className="landing-grain" aria-hidden="true" />

      <nav className="landing-nav" role="navigation">
        <span className="landing-nav-logo">
          <span className="landing-logo-main">ZIA</span>
          <span className="landing-logo-sub">PLATFORMS</span>
        </span>

        <div className="landing-nav-links">
          <button type="button" className="landing-nav-link" onClick={() => scrollTo('products')}>
            {t('nav.products')}
          </button>
          <button type="button" className="landing-nav-link" onClick={() => scrollTo('differentiators')}>
            {t('nav.differentiators')}
          </button>
          {/* <button type="button" className="landing-nav-link" onClick={() => scrollTo('pricing')}>
            {t('nav.pricing')}
          </button> */}
        </div>

        <div className="landing-nav-actions">
          <button
            type="button"
            className="landing-nav-lang landing-nav-lang--desktop"
            onClick={toggleLocale}
            aria-label={t('nav.langToggle')}
          >
            {t('nav.langToggle')}
          </button>
          <a href={ADMIN_LOGIN_URL} className="landing-nav-signin">
            {t('nav.signIn')}
          </a>
          <button
            type="button"
            className="landing-nav-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? t('nav.closeMenu') : t('nav.menu')}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
              {mobileMenuOpen ? (
                <path d="M5 5L15 15M15 5L5 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              ) : (
                <>
                  <line x1="3" y1="6" x2="17" y2="6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  <line x1="3" y1="10" x2="17" y2="10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  <line x1="3" y1="14" x2="17" y2="14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </>
              )}
            </svg>
          </button>
        </div>
      </nav>

      {mobileMenuOpen && (
        <div className="landing-mobile-menu">
          <button type="button" className="landing-mobile-menu-link" onClick={() => scrollTo('products')}>
            {t('nav.products')}
          </button>
          <button type="button" className="landing-mobile-menu-link" onClick={() => scrollTo('differentiators')}>
            {t('nav.differentiators')}
          </button>
          {/* <button type="button" className="landing-mobile-menu-link" onClick={() => scrollTo('pricing')}>
            {t('nav.pricing')}
          </button> */}
          <a
            href={ADMIN_LOGIN_URL}
            className="landing-mobile-menu-link"
            onClick={() => setMobileMenuOpen(false)}
          >
            {t('nav.signIn')}
          </a>
          <button
            type="button"
            className="landing-nav-lang landing-nav-lang--mobile"
            onClick={() => {
              toggleLocale();
              setMobileMenuOpen(false);
            }}
            aria-label={t('nav.langToggle')}
          >
            {t('nav.langToggle')}
          </button>
        </div>
      )}

      <section className="landing-hero" ref={heroRef as React.RefObject<HTMLElement>}>
        <HeroSvg />
        <span className="landing-hero-overline landing-reveal">{t('hero.overline')}</span>
        <h1 className="landing-hero-headline landing-reveal">
          <span className="landing-hero-headline-accent">{t('hero.headline')}</span>
        </h1>
        <p className="landing-hero-subtitle landing-reveal">{t('hero.subtitle')}</p>
        <div className="landing-hero-actions landing-reveal">
          <a href={ADMIN_LOGIN_URL} className="landing-hero-cta-primary">
            {t('hero.ctaPrimary')}
          </a>
          {/* <button type="button" className="landing-hero-cta-secondary" onClick={() => scrollTo('pricing')}>
            {t('hero.ctaSecondary')}
          </button> */}
        </div>
      </section>

      <div className="landing-trusted">
        <span className="landing-trusted-label">{t('trusted.title')}</span>
        <div className="landing-trusted-logos">
          {TRUSTED_LOGOS.map((name) => (
            <span key={name} className="landing-trusted-logo">{name}</span>
          ))}
        </div>
      </div>

      <section id="products" className="landing-section" ref={productsRef as React.RefObject<HTMLElement>}>
        <div className="landing-section-header landing-reveal">
          <h2 className="landing-section-title">{t('products.title')}</h2>
          <p className="landing-section-subtitle">{t('products.subtitle')}</p>
        </div>
        <div className="landing-products-grid landing-reveal-stagger">
          {PRODUCTS.map((product) => (
            <div key={product.key} className="landing-product-card landing-reveal">
              <div className={`landing-product-icon landing-product-icon--${product.colorClass}`}>
                <product.Icon />
              </div>
              <span className="landing-product-tagline">{t(`products.${product.key}.tagline`)}</span>
              <h3 className="landing-product-name">{t(`products.${product.key}.name`)}</h3>
              <p className="landing-product-description">{t(`products.${product.key}.description`)}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="landing-divider" />

      <section id="differentiators" className="landing-section" ref={diffRef as React.RefObject<HTMLElement>}>
        <div className="landing-section-header landing-reveal">
          <h2 className="landing-section-title">{t('differentiators.title')}</h2>
          <p className="landing-section-subtitle">{t('differentiators.subtitle')}</p>
        </div>
        <div className="landing-diff-grid landing-reveal-stagger">
          {DIFFERENTIATORS.map((diff) => (
            <div key={diff.key} className={`landing-diff-card landing-diff-card--${diff.colorClass} landing-reveal`}>
              <div className="landing-diff-card-glow" />
              <div className={`landing-diff-icon landing-diff-icon--${diff.colorClass}`}>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                  {diff.colorClass === 'privacy' && (
                    <path d="M10 2L16 5V10C16 14 13 17 10 18C7 17 4 14 4 10V5L10 2Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
                  )}
                  {diff.colorClass === 'configure' && (
                    <>
                      <circle cx="10" cy="10" r="3" stroke="currentColor" strokeWidth="1.3" />
                      <path d="M10 2V4M10 16V18M18 10H16M4 10H2M15.5 4.5L14 6M6 14L4.5 15.5M15.5 15.5L14 14M6 6L4.5 4.5" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" />
                    </>
                  )}
                  {diff.colorClass === 'bidirectional' && (
                    <>
                      <path d="M4 8H16M16 8L13 5M16 8L13 11" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M16 14H4M4 14L7 11M4 14L7 17" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
                    </>
                  )}
                  {diff.colorClass === 'memory' && (
                    <>
                      <ellipse cx="10" cy="14" rx="6" ry="3" stroke="currentColor" strokeWidth="1.2" />
                      <ellipse cx="10" cy="10" rx="5" ry="2.5" stroke="currentColor" strokeWidth="1.1" opacity="0.7" />
                      <ellipse cx="10" cy="6" rx="4" ry="2" stroke="currentColor" strokeWidth="1" opacity="0.5" />
                    </>
                  )}
                </svg>
              </div>
              <h3 className="landing-diff-title">{t(`differentiators.${diff.key}.title`)}</h3>
              <p className="landing-diff-description">{t(`differentiators.${diff.key}.description`)}</p>
              <diff.Svg />
            </div>
          ))}
        </div>
      </section>

      {/* <div className="landing-divider" />

      <section id="pricing" className="landing-section" ref={pricingRef as React.RefObject<HTMLElement>}>
        <div className="landing-section-header landing-reveal">
          <h2 className="landing-section-title">{t('pricing.title')}</h2>
          <p className="landing-section-subtitle">{t('pricing.subtitle')}</p>
        </div>
        <div className="landing-pricing-grid landing-reveal-stagger">
          {PRICING_TIERS.map(({ key, recommended }) => {
            const features = t(`pricing.${key}.features`, { returnObjects: true }) as string[];
            return (
              <div
                key={key}
                className={`landing-pricing-card landing-reveal ${recommended ? 'landing-pricing-card--recommended' : ''}`}
              >
                {recommended && (
                  <span className="landing-pricing-badge">{t('pricing.mostPopular')}</span>
                )}
                <h3 className="landing-pricing-name">{t(`pricing.${key}.name`)}</h3>
                <div className="landing-pricing-price">
                  <span className="landing-pricing-amount">{t(`pricing.${key}.price`)}</span>
                  {key !== 'enterprise' && (
                    <span className="landing-pricing-period">{t('pricing.monthly')}</span>
                  )}
                </div>
                <ul className="landing-pricing-features">
                  {features.map((feature, i) => (
                    <li key={`${key}-feature-${i}`} className="landing-pricing-feature">
                      <CheckIcon />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                {key === 'enterprise' ? (
                  <button type="button" className="landing-pricing-cta landing-pricing-cta--secondary">
                    {t('pricing.contactSales')}
                  </button>
                ) : (
                  <a
                    href={ADMIN_LOGIN_URL}
                    className={`landing-pricing-cta ${recommended ? 'landing-pricing-cta--primary' : 'landing-pricing-cta--secondary'}`}
                  >
                    {t('pricing.getStarted')}
                  </a>
                )}
              </div>
            );
          })}
        </div>
      </section>

      <div className="landing-divider" /> */}

      <section className="landing-cta-banner" ref={ctaRef as React.RefObject<HTMLElement>}>
        <div className="landing-cta-bg" aria-hidden="true" />
        <CtaSvg />
        <h2 className="landing-cta-title landing-reveal">{t('cta.title')}</h2>
        <p className="landing-cta-subtitle landing-reveal">{t('cta.subtitle')}</p>
        <div className="landing-cta-actions landing-reveal">
          <a href={ADMIN_LOGIN_URL} className="landing-hero-cta-primary">
            {t('cta.primary')}
          </a>
          <button type="button" className="landing-hero-cta-secondary">
            {t('cta.secondary')}
          </button>
        </div>
      </section>

      <footer className="landing-footer">
        <div className="landing-footer-grid">
          <div className="landing-footer-brand">
            <span className="landing-footer-logo">
              <span className="landing-logo-main">ZIA</span>
              <span className="landing-logo-sub">PLATFORMS</span>
            </span>
            <span className="landing-footer-tagline">{t('footer.tagline')}</span>
          </div>

          {FOOTER_COLUMN_KEYS.map((col) => (
            <div key={col}>
              <h4 className="landing-footer-col-title">{t(`footer.${col}.title`)}</h4>
              <ul className="landing-footer-col-links">
                {FOOTER_COL_LINKS[col].map((link) => (
                  <li key={link}>
                    <span className="landing-footer-link">{t(`footer.${col}.${link}`)}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="landing-footer-bottom">
          <span className="landing-footer-copyright">{t('footer.copyright')}</span>
        </div>
      </footer>
    </div>
  );
}
